import {configureStore, combineReducers} from '@reduxjs/toolkit'
import {useDispatch, useSelector} from 'react-redux'
import scenesReducer from './components/store/scenesSlice'
import undoable from './undoable'
import {installGaDispatchWrapper} from './analytics/gaReduxMiddleware'

const rootReducer = combineReducers({
  scenes: scenesReducer,
})

type AppStateInner = ReturnType<typeof rootReducer>

// Camera position and scene management actions bypass the undo stack —
// they update Redux in near real-time without creating undo entries.
// Device dimension actions are NOT transparent — they're only dispatched on
// slider commit (not during drag) and should create proper undo entries.
const TRANSPARENT_ACTIONS = new Set([
  'scenes/setCameraPosition',
  'scenes/setCameraPositionForScene',
  'scenes/setActiveScene',
  'scenes/addScene',
  'scenes/addNewScene',
  'scenes/removeScene',
  'scenes/renameScene',
  'scenes/loadProject',
  'scenes/setBgImageUploaded',
  'scenes/setCustomDecalUploaded',
  'scenes/applyScenePreset',
  'scenes/initializeWithDevice',
])

// Actions that auto-merge consecutive dispatches into one undo entry.
// Allows components to dispatch on every change (slider drag, pad move, etc.)
// without flooding the undo stack. Call `sealUndo()` on interaction end.
const MERGEABLE_ACTIONS = new Set([
  // Background
  'scenes/updateGradientStop',
  'scenes/setBgAngle',
  'scenes/setBgImageOffset',
  'scenes/setBgImageZoom',
  // Device dimensions
  'scenes/setDeviceWidth',
  'scenes/setDeviceHeight',
  'scenes/setDeviceDepth',
  'scenes/setDeviceCornerRadius',
  'scenes/setDeviceEdgeBevel',
  // Device transform — AnglePicker drag fires continuously (mirrors setBgAngle)
  'scenes/setDeviceRotationZ',
  'scenes/setDevicePositionZ',
  // Front face
  'scenes/setFrontFaceInset',
  'scenes/setDisplayCornerRadius',
  'scenes/setDisplayInsetX',
  'scenes/setDisplayInsetY',
  'scenes/setDisplayReflection',
  'scenes/updateFrontFaceFeature',
  // Side edges
  'scenes/updateSideEdgeFeature',
  // Materials — body
  'scenes/setBodyColor',
  'scenes/setBodyMetalness',
  'scenes/setBodyRoughness',
  'scenes/setBodyClearcoat',
  'scenes/setBodyClearcoatRoughness',
  'scenes/setBodyReflectivity',
  'scenes/setBodyNoiseIntensity',
  'scenes/setBodyNormalStrength',
  'scenes/setButtonsNoiseIntensity',
  'scenes/setButtonsNormalStrength',
  // Materials — front face
  'scenes/setFrontFaceColor',
  'scenes/setFrontFaceMetalness',
  'scenes/setFrontFaceRoughness',
  'scenes/setFrontFaceClearcoat',
  'scenes/setFrontFaceClearcoatRoughness',
  'scenes/setFrontFaceReflectivity',
  // Materials — buttons
  'scenes/setButtonsColor',
  'scenes/setButtonsMetalness',
  'scenes/setButtonsRoughness',
  'scenes/setButtonsClearcoat',
  'scenes/setButtonsClearcoatRoughness',
  'scenes/setButtonsReflectivity',
  // Materials — antennas
  'scenes/setAntennasColor',
  'scenes/setAntennasMetalness',
  'scenes/setAntennasRoughness',
  'scenes/setAntennasClearcoat',
  'scenes/setAntennasClearcoatRoughness',
  'scenes/setAntennasReflectivity',
  // Materials — screws
  'scenes/setScrewsColor',
  'scenes/setScrewsMetalness',
  'scenes/setScrewsRoughness',
  'scenes/setScrewsClearcoat',
  'scenes/setScrewsClearcoatRoughness',
  'scenes/setScrewsReflectivity',
  // Materials — front speaker
  'scenes/setFrontSpeakerColor',
  'scenes/setFrontSpeakerMetalness',
  'scenes/setFrontSpeakerRoughness',
  'scenes/setFrontSpeakerClearcoat',
  'scenes/setFrontSpeakerClearcoatRoughness',
  'scenes/setFrontSpeakerReflectivity',
  // Materials — speaker grill
  'scenes/setSpeakerGrillColor',
  'scenes/setSpeakerGrillMetalness',
  'scenes/setSpeakerGrillRoughness',
  'scenes/setSpeakerGrillClearcoat',
  'scenes/setSpeakerGrillClearcoatRoughness',
  'scenes/setSpeakerGrillReflectivity',
  // Materials — charging connector tongue
  'scenes/setChargingConnectorTongueColor',
  'scenes/setChargingConnectorTongueMetalness',
  'scenes/setChargingConnectorTongueRoughness',
  'scenes/setChargingConnectorTongueClearcoat',
  'scenes/setChargingConnectorTongueClearcoatRoughness',
  'scenes/setChargingConnectorTongueReflectivity',
  // Materials — charging connector rim
  'scenes/setChargingConnectorRimColor',
  'scenes/setChargingConnectorRimMetalness',
  'scenes/setChargingConnectorRimRoughness',
  'scenes/setChargingConnectorRimClearcoat',
  'scenes/setChargingConnectorRimClearcoatRoughness',
  'scenes/setChargingConnectorRimReflectivity',
  // Lighting
  'scenes/updateLight',
  'scenes/setAmbientIntensity',
  'scenes/setAmbientColor',
  // Environment
  'scenes/setEnvMapIntensity',
  // Ground
  'scenes/setGroundDistance',
  // Export — carousel gap slider
  'scenes/setExportCarouselGap',
  // Text
  'scenes/updateText',
  'scenes/updateText3D',
  // Sprites
  'scenes/updateSprite2D',
  'scenes/updateSprite3D',
  'scenes/updateSprite3DMaterial',
  // Custom decals
  'scenes/updateCustomDecal',
  'scenes/updateCustomDecalMaterial',
])

export const store = configureStore({
  reducer: undoable(rootReducer, {
    transparentActionTypes: TRANSPARENT_ACTIONS,
    mergeableActionTypes: MERGEABLE_ACTIONS,
    // Only snapshot the active scene — undo/redo never reverts structural changes
    // (adding/removing scenes, switching active scene).
    getUndoSlice: (state: AppStateInner) => state.scenes.scenes[state.scenes.activeSceneIndex],
    setUndoSlice: (state: AppStateInner, slice: unknown) => {
      const scenes = [...state.scenes.scenes]
      scenes[state.scenes.activeSceneIndex] = slice as (typeof scenes)[number]
      return {...state, scenes: {...state.scenes, scenes}}
    },
  }),
})

// GA analytics: monkey-patch `store.dispatch` to fan out every action through our
// action→event mapper. Done here instead of via configureStore's `middleware` option
// because providing that option in RTK 2.x degrades the inferred RootState to `any`
// (the middleware tuple's state generic widens through type unification).
installGaDispatchWrapper(store)

export type RootState = ReturnType<typeof store.getState>
export type AppState = ReturnType<typeof rootReducer>
export type AppDispatch = typeof store.dispatch

export const useAppDispatch = useDispatch.withTypes<AppDispatch>()
export const useAppSelector = useSelector.withTypes<RootState>()
